"use client";
import SectionWrapper, { SectionWrapperProps } from "@cc/section-wrapper";
import { Theme } from "@mui/material";
import { ReactNode } from "react";
import {
  MotionStack,
  MotionTypography,
  MotionTypographyProps,
} from "@cc/motion-components";
import ReactiveBreadcrumb from "@cc/breadcrumb";
import { deepmerge } from "@mui/utils";
import { StaticImageData } from "next/image";
import ArrowBannerBackground, {
  ArrowBannerBackgroundProps,
} from "@cc/arrow-banner-background";
import { isArray } from "lodash";
import {
  bottomToToptextStagger,
  bottomToToptextStaggerChildren,
} from "@cc/animations";
import { appbarHeight } from "@/layout/header/reactive-appbar";

export interface HeroBannerSectionProps
  extends Omit<SectionWrapperProps, "children" | "title"> {
  title?: ReactNode;
  TitleTypographyProps?: MotionTypographyProps;
  description?: ReactNode;
  gradient?: boolean;
  DescriptionTypographyProps?: MotionTypographyProps;
  disableBreadCrumb?: boolean;
  banner?: ArrowBannerBackgroundProps["images"] | string | StaticImageData;
}

// Todo: Update all banner with its type
const HeroBannerSection = (props: HeroBannerSectionProps) => {
  const {
    title,
    TitleTypographyProps,
    description,
    DescriptionTypographyProps,
    disableBreadCrumb,
    banner,
    gradient = true,
    ...restSectionWrapperProps
  } = props;
  return (
    <>
      <SectionWrapper
        {...deepmerge(
          defaultSectionWrapperProps,
          deepmerge(restSectionWrapperProps, {
            SectionProps: {
              sx: {
                background: (theme: Theme) =>
                  `${
                    gradient
                      ? theme.palette.gradient.darkToTransparentRight
                      : "none"
                  }, ${
                    banner &&
                    !isArray(banner) &&
                    (typeof banner === "string" || "src" in banner)
                      ? `${theme.palette.common.black} url(${
                          typeof banner === "string" ? banner : banner.src
                        })`
                      : ""
                  } no-repeat center`,
                backgroundSize: !isArray(banner) ?  "contain !important" : "cover !important",
                minHeight:!title ? {
                  xxl:`calc(100vh - 64px - ${appbarHeight.xxl})`,
                  md:`50vh`,
                  xs:`30vh`
                } : 'none'
              },
            },
          }),
        )}
      >
        {isArray(banner) && (
          <ArrowBannerBackground
            images={banner as ArrowBannerBackgroundProps["images"]}
          />
        )}
        {!disableBreadCrumb && <ReactiveBreadcrumb />}
        {(title || description) && (
          <MotionStack
            mt={23}
            maxWidth={863}
            rowGap={3}
            variants={bottomToToptextStagger(0.6)}
            initial={"initial"}
            animate={"animate"}
          >
            {title && (
              <MotionTypography
                variant="h1"
                variants={bottomToToptextStaggerChildren}
                {...TitleTypographyProps}
              >
                {title}
              </MotionTypography>
            )}
            {description && (
              <MotionTypography
                variant={"body2"}
                variants={bottomToToptextStaggerChildren}
                {...DescriptionTypographyProps}
              >
                {description}
              </MotionTypography>
            )}
          </MotionStack>
        )}
      </SectionWrapper>
    </>
  );
};

export default HeroBannerSection;

const defaultSectionWrapperProps: SectionWrapperProps = {
  SectionProps: {
    sx: {
      backgroundPosition: "center",
      backgroundSize: "cover",
      backgroundRepeat: "no-repeat",
      position: "relative",
    },
  },
  py: { xs: 2, md: 3, xxl: 4 },
  pb: { xs: 4, md: 6, xxl: 8 },
};
